"use client"

import type React from "react"
import { useState, useEffect } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { cn } from "@/shared/lib/utils"
import { AnimatePresence, motion } from "framer-motion"
import { Search } from "lucide-react"
import { ThemeToggle } from "@/shared/components/theme-toggle"
import { useInfoSitus } from "./info-situs-provider"
import { useProyekLain } from "@/modules/situs/hooks/use-proyek-lain"

const NAV_LINKS = [
  { href: "/", label: "Beranda" },
  { href: "/plans", label: "Paket" },
  { href: "/feature", label: "Fitur" },
  { href: "/history", label: "Riwayat" },
  { href: "/warranty", label: "Garansi" },
  { href: "/blog", label: "Blog" },
  { href: "/help", label: "Pusat bantuan" },
]

function HamburgerIcon({ open }: { open: boolean }) {
  return (
    <div className="w-4 h-3.5 relative flex flex-col justify-between">
      <span
        className={cn(
          "h-[1.5px] w-full bg-foreground rounded-full origin-center transition-transform duration-300 ease-[cubic-bezier(0.65,0,0.35,1)]",
          open && "rotate-45 translate-y-[6.5px]",
        )}
      />
      <span className={cn("h-[1.5px] w-full bg-foreground rounded-full transition-opacity duration-200", open && "opacity-0")} />
      <span
        className={cn(
          "h-[1.5px] w-full bg-foreground rounded-full origin-center transition-transform duration-300 ease-[cubic-bezier(0.65,0,0.35,1)]",
          open && "-rotate-45 -translate-y-[6.5px]",
        )}
      />
    </div>
  )
}

export default function Navbar() {
  const pathname = usePathname()
  const [isOpen, setIsOpen] = useState(false)
  const { projects, loadingProjects } = useProyekLain()
  const { info } = useInfoSitus()
  const { ownerName, appName, logoUrl } = info

  useEffect(() => {
    setIsOpen(false)
  }, [pathname])

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : ""
    return () => {
      document.body.style.overflow = ""
    }
  }, [isOpen])

  return (
    <header className="sticky top-0 z-50">
      <nav className="w-full flex items-center justify-between gap-2 bg-card/95 backdrop-blur border-b border-border px-4 py-3">
        <Link href="/" className="flex items-center gap-2 shrink-0">
          {logoUrl ? (
            <img src={logoUrl} alt={appName} className="w-8 h-8 object-contain rounded" />
          ) : (
            <img src="/icon/nobg.svg" alt={appName} className="w-8 h-8 object-contain" />
          )}
          <span className="text-base font-bold tracking-tight text-foreground">{appName}</span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map((link) => {
            const aktif = pathname === link.href
            return (
              <Link
                key={link.href}
                href={link.href}
                className={cn(
                  "px-3.5 py-2 text-sm font-medium transition-colors",
                  aktif ? "text-primary" : "text-muted-foreground hover:text-foreground",
                )}
              >
                {link.label}
              </Link>
            )
          })}
        </div>

        <div className="flex items-center gap-1">
          <Link
            href="/search"
            aria-label="Cari di situs"
            title="Cari"
            className="flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground active:bg-secondary"
          >
            <Search className="h-[1.1rem] w-[1.1rem]" aria-hidden="true" />
          </Link>
          <ThemeToggle />
          <button
            onClick={() => setIsOpen((v) => !v)}
            aria-label={isOpen ? "Tutup menu" : "Buka menu"}
            aria-expanded={isOpen}
            className="md:hidden flex h-9 w-9 items-center justify-center rounded-full active:bg-secondary transition-colors"
          >
            <HamburgerIcon open={isOpen} />
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25, ease: [0.65, 0, 0.35, 1] }}
            className="md:hidden fixed inset-x-0 top-[57px] bottom-0 z-40 bg-card flex flex-col"
          >
            <nav className="flex-1 flex flex-col items-center justify-center gap-1 px-6">
              {NAV_LINKS.map((link) => {
                const aktif = pathname === link.href
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    className={cn(
                      "px-6 py-3 text-base font-semibold transition-colors",
                      aktif ? "text-primary" : "text-foreground",
                    )}
                  >
                    {link.label}
                  </Link>
                )
              })}
            </nav>

            <div className="border-t border-border px-5 py-5 text-center">
              <span className="block text-xs font-medium text-muted-foreground mb-3">Juga dari {ownerName || "kami"}</span>
              <div className="flex flex-wrap justify-center gap-4">
                {!loadingProjects ? (
                  projects.map((project, idx) => (
                    <a
                      key={idx}
                      href={project.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex flex-col items-center gap-1 w-14 text-center group"
                    >
                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-secondary group-hover:bg-accent transition-colors overflow-hidden shrink-0">
                        {project.favicon && (
                          <img
                            src={project.favicon}
                            alt={project.name}
                            className="w-5 h-5 object-contain"
                            onError={(e) => {
                              ;(e.target as HTMLImageElement).src = `${new URL(project.url).origin}/favicon.ico`
                            }}
                          />
                        )}
                      </div>
                      <span className="text-[10px] font-medium text-muted-foreground group-hover:text-foreground transition-colors truncate w-full">
                        {project.name}
                      </span>
                    </a>
                  ))
                ) : (
                  <div className="flex gap-4">
                    {[1, 2, 3].map((s) => (
                      <div key={s} className="flex flex-col items-center gap-1 w-14 animate-pulse">
                        <div className="w-10 h-10 bg-secondary rounded-full" />
                        <div className="w-8 h-2 bg-secondary rounded" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
